import React, { useState } from "react";
import { motion } from "framer-motion";
import { MOCK_STATS, MOCK_PROJECTS, MOCK_DONATIONS, MOCK_DONATION_TRENDS, MOCK_CURRENCY_DATA, MOCK_COUNTRY_DATA } from "../lib/mockData";
import { GlobalMap } from "../components/dashboard/GlobalMap";
import { LiveDonationFeed } from "../components/dashboard/LiveDonationFeed";
import { DonationTrendChart, CurrencyDistributionChart, CountryDistributionChart } from "../components/dashboard/Charts";
import { HumaTekFooter } from "../components/layout/HumaTekFooter";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import {
  Globe,
  Heart,
  Building2,
  Users,
  TrendingUp,
  RefreshCw,
  MapPin,
  BarChart3,
  DollarSign,
  Target,
} from "lucide-react";

const LOGO_URL = "https://cdn.gamma.app/40wtxlv020nnuu2/0bd85e15bbd243ca96d4ed4e00a0f4fa/original/HumaDash-Logo-3.4.25.png";

export default function PublicDashboard() {
  const stats = MOCK_STATS;
  const mapData = MOCK_PROJECTS;

  return (
    <div className="min-h-screen bg-[#F4F5F7]" data-testid="public-dashboard">
      <div className="pt-20 pb-12 px-4">
        <div className="container mx-auto max-w-7xl">
          {/* Header */}
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-8"
          >
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              <div className="flex items-center gap-4">
                <img src={LOGO_URL} alt="HumaDash" className="h-12 w-auto" />
                <div>
                  <h1 className="font-heading font-bold text-2xl sm:text-3xl text-slate-800" data-testid="dashboard-title">
                    Global Aid Transparency Dashboard
                  </h1>
                  <p className="text-slate-500">
                    Real-time tracking of humanitarian donations worldwide
                  </p>
                </div>
              </div>
              <Badge variant="outline" className="bg-green-50 text-green-600 border-green-200">
                <span className="h-2 w-2 rounded-full bg-green-500 mr-2 animate-pulse" />
                Live
              </Badge>
            </div>
          </motion.div>

          {/* Three-Column Dashboard Layout */}
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
            {/* LEFT COLUMN: Core Financial & Impact Metrics */}
            <div className="lg:col-span-3 space-y-4" data-testid="metrics-column">
              {/* Gradient Total Donations Card */}
              <div className="rounded-xl bg-gradient-to-br from-[#6B21A8] to-[#3B82F6] p-5 text-white shadow-lg" data-testid="total-donations-card">
                <div className="flex items-center gap-3 mb-3">
                  <div className="h-10 w-10 rounded-lg bg-white/20 flex items-center justify-center shrink-0">
                    <DollarSign className="h-5 w-5 text-white" />
                  </div>
                  <p className="text-sm font-medium text-white/80">Total Donations</p>
                </div>
                <p className="text-3xl font-heading font-black text-white">
                  ${stats.total_donations.toLocaleString()}
                </p>
                <p className="text-xs text-white/60 mt-1">All-time verified contributions</p>
              </div>

              <Card className="bg-white shadow-sm">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-heading text-slate-400 uppercase tracking-wider">Financial Overview</CardTitle>
                </CardHeader>
                <CardContent className="space-y-5">
                  <div className="flex items-center gap-3">
                    <div className="h-10 w-10 rounded-lg bg-green-500/10 flex items-center justify-center shrink-0">
                      <TrendingUp className="h-5 w-5 text-green-600" />
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground">Donated Today</p>
                      <p className="text-xl font-heading font-bold text-foreground">
                        ${stats.donations_today.toLocaleString()}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="h-10 w-10 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                      <Heart className="h-5 w-5 text-primary" />
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground">Active Donors</p>
                      <p className="text-xl font-heading font-bold text-foreground">{stats.total_donors}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-white shadow-sm">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-heading text-slate-400 uppercase tracking-wider">Impact Metrics</CardTitle>
                </CardHeader>
                <CardContent className="space-y-5">
                  <div className="flex items-center gap-3">
                    <div className="h-10 w-10 rounded-lg bg-primary/10 flex items-center justify-center shrink-0"><MapPin className="h-5 w-5 text-primary" /></div>
                    <div><p className="text-xs text-muted-foreground">Active Projects</p><p className="text-xl font-heading font-bold">{stats.total_projects}</p></div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="h-10 w-10 rounded-lg bg-primary/10 flex items-center justify-center shrink-0"><Building2 className="h-5 w-5 text-primary" /></div>
                    <div><p className="text-xs text-muted-foreground">Organizations</p><p className="text-xl font-heading font-bold">{stats.total_organizations}</p></div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="h-10 w-10 rounded-lg bg-primary/10 flex items-center justify-center shrink-0"><Users className="h-5 w-5 text-primary" /></div>
                    <div><p className="text-xs text-muted-foreground">Beneficiaries Reached</p><p className="text-xl font-heading font-bold">{stats.beneficiaries_reached.toLocaleString()}</p></div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="h-10 w-10 rounded-lg bg-primary/10 flex items-center justify-center shrink-0"><Globe className="h-5 w-5 text-primary" /></div>
                    <div><p className="text-xs text-muted-foreground">Countries Served</p><p className="text-xl font-heading font-bold">{stats.countries_served}</p></div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="h-10 w-10 rounded-lg bg-green-500/10 flex items-center justify-center shrink-0"><Target className="h-5 w-5 text-green-600" /></div>
                    <div><p className="text-xs text-muted-foreground">Projects Completed</p><p className="text-xl font-heading font-bold">{stats.projects_completed}</p></div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* CENTER COLUMN: Interactive Global Map */}
            <div className="lg:col-span-6" data-testid="map-column">
              <Card className="bg-white shadow-sm h-full">
                <CardHeader className="pb-2">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2"><Globe className="h-5 w-5 text-primary" /><CardTitle className="font-heading">Global Project Map</CardTitle></div>
                    <Badge variant="secondary" className="bg-primary/10 text-primary border-primary/20">{mapData.length} Active Projects</Badge>
                  </div>
                </CardHeader>
                <CardContent className="p-0">
                  <GlobalMap projects={mapData} height="580px" />
                </CardContent>
              </Card>
            </div>

            {/* RIGHT COLUMN: Live Allocation Feed */}
            <div className="lg:col-span-3" data-testid="feed-column">
              <Card className="bg-white shadow-sm h-full">
                <CardHeader className="pb-2">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2"><TrendingUp className="h-5 w-5 text-primary" /><CardTitle className="font-heading">Live Allocations</CardTitle></div>
                    <Badge variant="outline" className="text-xs bg-green-50 text-green-600 border-green-200">
                      <span className="h-1.5 w-1.5 rounded-full bg-green-500 mr-1.5 animate-pulse" />Live
                    </Badge>
                  </div>
                </CardHeader>
                <CardContent className="max-h-[580px] overflow-y-auto custom-scrollbar">
                  <LiveDonationFeed limit={15} />
                </CardContent>
              </Card>
            </div>
          </div>

          {/* Analytics Section */}
          <div className="mt-8">
            <Tabs defaultValue="trends" className="w-full">
              <TabsList className="mb-4">
                <TabsTrigger value="trends" data-testid="tab-trends"><BarChart3 className="h-4 w-4 mr-2" />Trends</TabsTrigger>
                <TabsTrigger value="currency" data-testid="tab-currency">Currency</TabsTrigger>
                <TabsTrigger value="geography" data-testid="tab-geography">Geography</TabsTrigger>
              </TabsList>
              <TabsContent value="trends"><DonationTrendChart data={MOCK_DONATION_TRENDS} title="Donation Volume (Last 30 Days)" /></TabsContent>
              <TabsContent value="currency"><CurrencyDistributionChart data={MOCK_CURRENCY_DATA} /></TabsContent>
              <TabsContent value="geography"><CountryDistributionChart data={MOCK_COUNTRY_DATA} /></TabsContent>
            </Tabs>
          </div>

          <div className="mt-12 text-center">
            <p className="text-sm text-slate-500">Data verified by blockchain &middot; Last updated: {new Date().toLocaleString()}</p>
          </div>
        </div>
      </div>
      <HumaTekFooter />
    </div>
  );
}
